import type { CinematicOverrides } from '../../../renderer/src/cinematic-sequence';
import { BLACKBIRD_TRACE_COLOR, defaultTrackAppearance } from '../../../renderer/src/track-appearance';
import { BLACKBIRD_ALTITUDE, BLACKBIRD_LENS, BLACKBIRD_TILT } from '../domain/blackbird-camera';
import { cameraLensMillimeters } from '../domain/camera-keyframes';
import { automaticCameraAttributes } from '../map/attribute-camera';
import type { CameraPose } from '../domain/camera-plan';
import type { CinematicSettings } from '../domain/cinematic-plan';
import { upsertAttributeKey } from './camera-tracks/camera-animation';
import type { StudioDraft } from './studio-draft';

/** One preset selection writes both scene attributes at the same authored frame. */
export const blackbirdOverrides = (): CinematicOverrides => ({ cameraStyle: 'blackbird', mapStyle: 'blackbird', cameraZoom: null });

export function blackbirdStartingDraft(draft: StudioDraft): StudioDraft {
  const defaultColor = defaultTrackAppearance(draft.line, draft.camera, draft.mapStyle).color;
  return { ...draft, camera: 'blackbird', mapStyle: 'blackbird', terrainExaggeration: 1,
    trackAppearance: draft.trackAppearance.color.toLowerCase() === defaultColor.toLowerCase()
      ? { ...draft.trackAppearance, color: BLACKBIRD_TRACE_COLOR } : draft.trackAppearance };
}

/** Preserve authored axes; seed only attributes that still use automatic behavior. */
export function initializeBlackbird(draft: StudioDraft, pose: CameraPose, settings: CinematicSettings,
  progress: number, duration: number): StudioDraft {
  const seconds = progress * duration;
  const base = automaticCameraAttributes(pose, cameraLensMillimeters(settings.cameraDistance), 1080);
  let tracks = draft.cinematicSequence.cameraTracks;
  for (const [axis, value] of [['lens', BLACKBIRD_LENS], ['altitude', BLACKBIRD_ALTITUDE], ['tilt', BLACKBIRD_TILT]] as const) {
    if (tracks?.[axis].length) continue;
    tracks = upsertAttributeKey(tracks, axis, seconds, value, base[axis], duration).tracks;
  }
  const defaultColor = defaultTrackAppearance(draft.line, settings.cameraStyle, settings.mapStyle).color;
  const trackAppearance = draft.trackAppearance.color.toLowerCase() === defaultColor.toLowerCase()
    ? { ...draft.trackAppearance, color: BLACKBIRD_TRACE_COLOR } : draft.trackAppearance;
  return { ...draft, trackAppearance, terrainExaggeration: 1,
    cinematicSequence: { ...draft.cinematicSequence, ...(tracks ? { cameraTracks: tracks } : {}) } };
}
